import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const FALLBACK_USERS = [
  { email: 'chasseur@test.ca', label: 'Hunter', role: 'hunter' },
  { email: 'chasseur2@test.ca', label: 'Hunter #2', role: 'hunter' },
  { email: 'agriculteur@test.ca', label: 'Farmer', role: 'farmer' },
  { email: 'proprietaire@test.ca', label: 'Land owner', role: 'landowner' },
  { email: 'guide@test.ca', label: 'Guide', role: 'guide' },
];

const ADMIN_DASHBOARD_URL = '/admin';

const ROLE_COLORS = {
  hunter: '#16a34a',
  farmer: '#ca8a04',
  landowner: '#7c3aed',
  guide: '#0891b2',
};

export default function DevUserSwitcher() {
  // Hooks must run in the same order on every render — call them before
  // the prod-mode early-return below (was `if (!import.meta.env.DEV)
  // return null;` before any useState/useEffect, which violated the
  // Rules of Hooks). In prod, import.meta.env.DEV is statically `false`
  // and Vite tree-shakes the whole component out of the bundle anyway,
  // so this reorder is purely correctness — runtime cost is unchanged.
  const { utilisateur, connecter, deconnecter } = useAuth();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState(FALLBACK_USERS);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    api
      .get('/dev/users')
      .then((r) => {
        if (Array.isArray(r.data?.users)) setUsers(r.data.users);
      })
      .catch(() => {});
  }, []);

  if (!import.meta.env.DEV) return null;

  const loginAs = async (email) => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.post('/dev/login-as', { email });
      connecter(data.token, data.user);
      window.location.reload();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setBusy(false);
    }
  };

  const logout = () => {
    deconnecter();
    window.location.reload();
  };

  return (
    <div style={S.wrap}>
      {open && (
        <div style={S.panel}>
          <div style={S.header}>
            <strong style={{ fontSize: 13 }}>Dev — switch user</strong>
            <button style={S.close} onClick={() => setOpen(false)}>
              ×
            </button>
          </div>
          <div style={S.current}>
            {utilisateur ? (
              <>
                Logged in as <strong>{utilisateur.email}</strong>
                <br />
                <span style={{ color: '#666' }}>{utilisateur.user_type}</span>
              </>
            ) : (
              <span style={{ color: '#666' }}>Not logged in</span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {users.map((u) => {
              const active = utilisateur?.email === u.email;
              return (
                <button
                  key={u.email}
                  onClick={() => loginAs(u.email)}
                  disabled={busy || active}
                  style={{
                    ...S.userBtn,
                    borderLeft: `4px solid ${ROLE_COLORS[u.role] || '#888'}`,
                    background: active ? '#eef2ff' : '#fff',
                    cursor: active ? 'default' : 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{u.label}</div>
                  <div style={{ fontSize: 11, color: '#555' }}>{u.email}</div>
                  <div
                    style={{
                      fontSize: 10,
                      color: ROLE_COLORS[u.role] || '#888',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {u.role}
                  </div>
                </button>
              );
            })}
          </div>
          <a href={ADMIN_DASHBOARD_URL} target="_blank" rel="noreferrer" style={S.adminLink}>
            Admin? Open /admin dashboard →
          </a>
          {utilisateur && (
            <button onClick={logout} disabled={busy} style={S.logout}>
              Logout
            </button>
          )}
          {err && <div style={S.err}>{err}</div>}
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} style={S.fab} title="Dev user switcher">
        🔧 Dev
      </button>
    </div>
  );
}

const S = {
  wrap: {
    position: 'fixed',
    bottom: 16,
    right: 16,
    zIndex: 99999,
    fontFamily: 'system-ui, sans-serif',
  },
  fab: {
    background: '#7c3aed',
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  panel: {
    position: 'absolute',
    bottom: 44,
    right: 0,
    width: 260,
    background: '#fafafa',
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: 10,
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  close: {
    background: 'transparent',
    border: 'none',
    fontSize: 18,
    cursor: 'pointer',
    color: '#666',
    lineHeight: 1,
  },
  current: {
    fontSize: 12,
    padding: 8,
    background: '#fff',
    borderRadius: 4,
    marginBottom: 8,
    border: '1px solid #eee',
  },
  userBtn: { textAlign: 'left', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4 },
  adminLink: {
    display: 'block',
    marginTop: 8,
    padding: '6px 8px',
    fontSize: 11,
    color: '#7c3aed',
    textDecoration: 'none',
    textAlign: 'center',
    background: '#f5f3ff',
    border: '1px solid #ddd6fe',
    borderRadius: 4,
  },
  logout: {
    marginTop: 6,
    width: '100%',
    padding: 6,
    background: '#fff',
    border: '1px solid #dc2626',
    color: '#dc2626',
    borderRadius: 4,
    fontSize: 12,
    cursor: 'pointer',
  },
  err: {
    marginTop: 8,
    padding: 6,
    background: '#fee2e2',
    color: '#991b1b',
    fontSize: 11,
    borderRadius: 4,
  },
};
